import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { leaderboardAPI } from '@/utils/api';
import { useLang } from '@/utils/i18n';
import { ArrowLeft, Trophy, Medal, Flame, Crown, Star, TrendingUp } from 'lucide-react';
import LangSwitcher from '@/components/LangSwitcher';
import { MobileBottomNav } from '@/components/MobileNav';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [tab, setTab] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData(tab);
  }, [tab]);

  const loadData = async (type) => {
    setLoading(true);
    try {
      const res = type === 'monthly'
        ? await leaderboardAPI.getMonthly()
        : await leaderboardAPI.getAllTime();
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const rankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-mono text-gray-500">#{rank}</span>;
  };

  const rankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/15 to-yellow-900/5 border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/10 to-gray-800/5 border-gray-400/20';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/10 to-amber-900/5 border-amber-600/20';
    return 'bg-[#1E1E1E] border-white/10';
  };

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0" data-testid="leaderboard-page">
      {/* Header */}
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="lb-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>
          <h1 className="text-lg font-bold text-[#00BFA6] flex items-center gap-2">
            <Trophy className="w-5 h-5" /> {t('leaderboard')}
          </h1>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* My Rank Card */}
        {data?.my_rank && (
          <Card className="bg-gradient-to-r from-[#7B1FA2]/30 to-[#4A148C]/20 border-[#7B1FA2]/40" data-testid="my-rank-card">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">{t('your_rank')}</div>
                <div className="text-4xl font-mono font-bold text-[#00BFA6]">#{data.my_rank.rank}</div>
                <div className="text-xs text-gray-500">{t('of')} {data.my_rank.total} {t('participants')}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">{data.my_rank.score}</div>
                <div className="text-xs text-gray-400">{t('score')}</div>
                <div className="text-sm text-[#00BFA6] mt-1">{data.my_rank.accuracy}% {t('hit_rate')}</div>
              </div>
              {data.my_rank.badges?.length > 0 && (
                <div className="flex flex-col gap-1">
                  {data.my_rank.badges.map((b) => (
                    <span key={b.id} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: b.color, backgroundColor: `${b.color}20`, border: `1px solid ${b.color}40` }}>
                      {b.label}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2" data-testid="lb-tabs">
          <button
            onClick={() => setTab('monthly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === 'monthly' ? 'bg-[#00BFA6] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="lb-tab-monthly"
          >
            {t('monthly')}
          </button>
          <button
            onClick={() => setTab('all-time')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === 'all-time' ? 'bg-[#00BFA6] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="lb-tab-alltime"
          >
            {t('all_time')}
          </button>
          {data?.month && tab === 'monthly' && (
            <span className="text-sm text-gray-500 self-center ml-2">{data.month}</span>
          )}
        </div>

        {/* Rankings */}
        {loading ? (
          <div className="text-center py-12 text-[#00BFA6]">{t('loading')}</div>
        ) : data?.rankings?.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="lb-empty">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>{t('no_rankings')}</p>
            <p className="text-xs text-gray-600 mt-1">{t('no_rankings_desc')}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="lb-rankings">
            {data?.rankings?.map((r) => (
              <Card key={r.rank} className={`border transition-all hover:scale-[1.01] ${rankBg(r.rank)}`} data-testid={`lb-rank-${r.rank}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  {/* Rank */}
                  <div className="shrink-0 w-10 text-center">{rankIcon(r.rank)}</div>

                  {/* Avatar */}
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                    r.streak >= 3 ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' : 'bg-[#2A2A2A] text-gray-400'
                  }`}>
                    {r.streak >= 3 ? <Flame className="w-5 h-5" /> : r.anon_name.charAt(0)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{r.anon_name}</span>
                      {r.streak >= 3 && (
                        <span className="text-[10px] font-bold text-orange-400 bg-orange-500/20 px-1.5 py-0.5 rounded-full">
                          {r.streak}x
                        </span>
                      )}
                      {r.badges?.map((b) => (
                        <span key={b.id} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: b.color, backgroundColor: `${b.color}15`, border: `1px solid ${b.color}30` }}>
                          {b.label}
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.total_tips} {t('tips')} &middot; {r.won}W {r.lost}L {r.pending > 0 ? `${r.pending}P` : ''}
                    </div>
                  </div>

                  {/* Score + Accuracy */}
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-lg">{r.score}</div>
                    <div className="text-xs text-[#00BFA6]">{r.accuracy}%</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 p-4 bg-[#1E1E1E] border border-white/10 rounded-xl">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">{t('badges_legend')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              { icon: <Crown className="w-3 h-3" />, color: '#FFD700', label: 'Gold Analyst', desc: '70%+ / 10+ Tipps' },
              { icon: <Medal className="w-3 h-3" />, color: '#C0C0C0', label: 'Silver Analyst', desc: '60%+ / 7+ Tipps' },
              { icon: <Medal className="w-3 h-3" />, color: '#CD7F32', label: 'Bronze Analyst', desc: '50%+ / 5+ Tipps' },
              { icon: <Flame className="w-3 h-3" />, color: '#FF6B35', label: 'Hot Streak', desc: '5+ Serie' },
              { icon: <Star className="w-3 h-3" />, color: '#7B1FA2', label: 'Veteran', desc: '20+ Tipps' },
              { icon: <TrendingUp className="w-3 h-3" />, color: '#00BFA6', label: 'Newcomer', desc: '1-5 Tipps' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span style={{ color: b.color }}>{b.icon}</span>
                <span style={{ color: b.color }} className="font-medium">{b.label}</span>
                <span className="text-gray-600">{b.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default LeaderboardPage;
